import React, {useCallback, useEffect, useState} from 'react';
import {SideBarModalProps} from '../../../../../layout/common/SideBarModal';
import {Button, Grid, Checkbox} from '@mui/material';
import {AutocompleteControl, Form, TextFieldControl, useForm} from '../../../../../core/Form';
import {FormControl, FormGroup} from 'react-reactive-form';
import CancelButton from '../../../../../core/Buttons/CancelButton';
import {ExecutionSetupDTO} from 'hemwb-api';
import GridTitle from '../../../../../core/Form/GridTitle';
import {UserProfile} from '@carbon/pictograms-react';
import GridItem from '../../../../../core/Form/GridItem';
import {getCommonFieldProps, hardCodedOptions} from '../ModelEditMetadata/constants';
import {
  userExpertiseLoadList,
  userExpertiseLoadOptionsSet,
  useUserExpertise,
} from '../../../../../../store/userExpertise';
import TableWithSortAndPagination from '../../../../../core/TableWithSortAndPagination';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {Order, useGridState} from '../../../../../../store/grid';
import {useDispatch} from 'react-redux';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {subModelGridColumns} from '../../../../subModel/list/utils';
import {PropertySelection} from '../../../../../pages/admin/externalization/enroll/SubModelEnrollForm';

type ExpertiseModalProps = SideBarModalProps & {
  user?: any;
  modelUuid?: string;
  handleUpdateSubModelIds?: (id: number, selected: boolean) => void;
  handleCancelSubModelIds?: () => void;
  selectedIds?: number[];
  disableCheckbox: () => boolean | undefined;
  storedCalibrationList?: ExecutionSetupDTO[] | null;
  storedScenarioList?: ExecutionSetupDTO[] | null;
};

const UserSettingsForm: React.FC<ExpertiseModalProps> = (props) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<PropertySelection>();
  const disable = props.disableCheckbox();

  const displayCheckBox = (subModel: ExecutionSetupDTO) => <CheckBox subModel={subModel} />;
  const CheckBox: React.FC<{subModel: ExecutionSetupDTO}> = ({subModel}) => (
    <>
      <div>
        <div>
          <Checkbox
            key="selected"
            color="primary"
            disabled={disable}
            checked={selected && (selected[subModel.id] || false)}
            onChange={(event, checked) => {
              setSelected({...selected, [subModel.id]: checked});
              props.handleUpdateSubModelIds?.(subModel.id, checked);
            }}
          />
        </div>
      </div>
    </>
  );

  const userSettingsColumns = [
    subModelGridColumns[0],
    subModelGridColumns[1],
    subModelGridColumns[2],
    {
      id: 'button',
      label: '',
      sortable: false,
      display: displayCheckBox,
    },
  ];

  const modelUuid = props.modelUuid;

  const userid = props.user.id;

  const expertiseLevel = useUserExpertise(modelUuid, userid);

  const gridState = useGridState({orderBy: 'name', order: Order.ASC});

  const form = useForm(
    new FormGroup({
      firstName: new FormControl(props.user.firstName),
      lastName: new FormControl(props.user.lastName),
      email: new FormControl(props.user.email),
      id: new FormControl(props.user.id),
      expertiseLevel: new FormControl(null),
    }),
  );

  useEffect(() => {
    form.controls['expertiseLevel'].setValue(expertiseLevel?.expertiseTypeLevel);
    const accessedSubModels: PropertySelection = {};
    if (disable && expertiseLevel?.subModelIdList) {
      expertiseLevel?.subModelIdList.forEach((item: number) => {
        props.handleUpdateSubModelIds?.(item, true);
      });
    }
    if (expertiseLevel?.subModelIdList && selected === undefined) {
      expertiseLevel?.subModelIdList.forEach((item: number) => {
        accessedSubModels[item] = true;
        props.handleUpdateSubModelIds?.(item, true);
      });
      setSelected(accessedSubModels);
    }
  }, [form, expertiseLevel, selected, disable, props.handleUpdateSubModelIds]);

  const handleClose = useCallback(() => {
    props.closeModal();
  }, [props]);

  const handleSubmit = useCallback(() => {
    return userExpertiseLoadOptionsSet(dispatch, modelUuid, true, {
      userId: props.user.id,
      expertiseTypeLevel: form.controls.expertiseLevel.value,
      subModelIdList: props.selectedIds,
    }).then(() => {
      dispatch(messageAdd('User setting updated', MessageTypes.SUCCESS));
      props.handleCancelSubModelIds?.();
      props.closeModal();
      return userExpertiseLoadList(dispatch, modelUuid, props.user.id);
    });
  }, [dispatch, modelUuid, props, form.controls]);

  return (
    <>
      <Form
        className=""
        group={form}
        render={() => {
          return (
            <>
              <Grid
                container
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                style={{paddingLeft: '3rem'}}
              >
                <Grid container direction="column" style={{paddingRight: '3rem', marginBottom: '3rem'}}>
                  <GridTitle Icon={<UserProfile className="icon32" />}>
                    Expertise Level
                    <br />
                    <small style={{fontSize: 12}}>Assign expertise Level</small>
                  </GridTitle>
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem', marginBottom: '3rem'}}>
                  <Grid>
                    <GridItem>
                      <TextFieldControl name="firstName" label="First Name" disabled={true} />
                    </GridItem>
                  </Grid>
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem', marginBottom: '3rem'}}>
                  <Grid>
                    <GridItem>
                      <TextFieldControl name="lastName" label="Last Name" disabled={true} />
                    </GridItem>
                  </Grid>
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem', marginBottom: '3rem'}}>
                  <Grid>
                    <GridItem>
                      <TextFieldControl name="email" label="Email" disabled={true} />
                    </GridItem>
                  </Grid>
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem'}}>
                  <Grid>
                    <GridItem>
                      <TextFieldControl name="id" label="ISID" disabled={true} />
                    </GridItem>
                  </Grid>
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem'}}>
                  <Grid>
                    <GridItem>
                      <AutocompleteControl
                        {...getCommonFieldProps('expertiseLevel')}
                        AutocompleteProps={{
                          getOptionLabel: (option: string) => option || '',
                          options: hardCodedOptions['expertiseLevel'],
                        }}
                      />
                    </GridItem>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                style={{paddingLeft: '3rem'}}
              >
                <Grid container direction="column" style={{paddingRight: '3rem', marginBottom: '3rem'}}>
                  <GridTitle Icon={<UserProfile className="icon32" />}>
                    Sub-models Access
                    <br />
                    <small style={{fontSize: 12}}>Assign sub-models access to the user</small>
                  </GridTitle>
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem'}}>
                  <GridTitle>Calibration Management</GridTitle>
                </Grid>

                <Grid>
                  <TableWithSortAndPagination<ExecutionSetupDTO>
                    getRowId={(o) => o.id}
                    rows={props.storedCalibrationList || []}
                    columns={userSettingsColumns}
                    gridState={gridState}
                    hidePagination={true}
                    noResultsMessage="No SubModel was found"
                  />
                </Grid>
                <Grid container direction="column" style={{paddingRight: '3rem'}}>
                  <GridTitle>Scenario Management</GridTitle>
                </Grid>

                <Grid>
                  <TableWithSortAndPagination<ExecutionSetupDTO>
                    getRowId={(o) => o.id}
                    rows={props.storedScenarioList || []}
                    columns={userSettingsColumns}
                    gridState={gridState}
                    hidePagination={true}
                    noResultsMessage="No SubModel was found"
                  />
                </Grid>
              </Grid>
              <div className="buttonsContainer">
                <br />
                <br />
                <CancelButton onClick={handleClose} />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  type="button"
                  size="large"
                  endIcon={
                    <CheckCircleOutlineIcon style={{width: '1.6rem', height: '1.6rem'}}> </CheckCircleOutlineIcon>
                  }
                >
                  Update Settings
                </Button>
              </div>
            </>
          );
        }}
      />
    </>
  );
};
export default UserSettingsForm;
